import styles from './SearchSuggestions.module.scss';
import React, { forwardRef, useEffect } from 'react';
import { FontAwesomeProIcon, Link } from '@wienenergiegithub/ui-next';
import { useTranslation } from '@wienenergiegithub/modules-next/hooks';
import classNames from 'classnames';

const SearchSuggestions = forwardRef(
  ({ suggestions, isSearchOpen, id, selectedIndex, setSelectedIndex }, ref) => {
    const { t } = useTranslation('suggestions');

    const { suggestionsRef, inputRef } = ref.current;

    // custom logics for keyboard events
    const handleKeyPress = event => {
      switch (event.key) {
        case 'ArrowUp':
          // prevent general page scrolling
          event.preventDefault();
          if (selectedIndex > 0) {
            setSelectedIndex(prev => prev - 1);
            suggestionsRef[selectedIndex - 1].focus();
          }
          if (selectedIndex === 0) {
            // move to input field if at very top of suggestions list
            inputRef.focus();
          }
          break;
        case 'ArrowDown':
          // prevent general page scrolling
          event.preventDefault();
          if (selectedIndex < suggestions.length - 1) {
            setSelectedIndex(prev => prev + 1);
            suggestionsRef[selectedIndex + 1].focus();
          }
          break;
        case 'Enter':
          event.preventDefault();
          suggestionsRef[selectedIndex].click();
          break;
        case 'Tab':
          if (event.shiftKey) {
            if (selectedIndex === 0) {
              // move to input field if at very top of suggestions list
              event.preventDefault();
              inputRef.focus();
            }
            if (selectedIndex > 0) {
              setSelectedIndex(prev => prev - 1);
            }
          } else if (selectedIndex < suggestions.length - 1) {
            setSelectedIndex(prev => prev + 1);
          }
          break;
        default:
          break;
      }
    };

    useEffect(() => {
      // reset selected index if search is not open
      if (!isSearchOpen) {
        setSelectedIndex(0);
      }
    }, [isSearchOpen]);

    return (
      <ul
        aria-hidden={!isSearchOpen}
        aria-label={t('suggestions')}
        className={classNames(styles.suggestionsList, {
          [styles.isSearchOpen]: isSearchOpen,
        })}
        id={id}
        onKeyDown={handleKeyPress}
        role="listbox"
      >
        {suggestions?.map((item, index) => (
          <li
            aria-selected={selectedIndex === index}
            id={item.id}
            key={item.id}
            role="option"
          >
            <Link
              className={styles.suggestionItemLink}
              ref={el => {
                suggestionsRef[index] = el;
              }}
              to={item.url}
            >
              {item.title}
              <FontAwesomeProIcon
                aria-hidden
                className={styles.chevronRight}
                icon="chevron-right"
                role="presentation"
              />
            </Link>
          </li>
        ))}
      </ul>
    );
  },
);

SearchSuggestions.displayName = 'SearchSuggestions';

export default SearchSuggestions;
